import { EnvironmentService } from "./environment.service";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, map, distinctUntilChanged } from "rxjs";
import { VCPM } from 'pcs-domain';
import { PcsSizingEngineResponse } from 'pcs-domain';
import { GroupedComponent } from "../../feature-modules/output-components/management-domain/management-domain.component";
import { AppConstants } from "../data-models/app-constants";

@Injectable({
  providedIn: 'root',
})
export class PCSSizerSerive {

  config: any;
   private _nextButton$ = new BehaviorSubject<boolean>(false);
   private readonly childSaves = new BehaviorSubject<ReadonlyMap<object, string>>(new Map());
  constructor(
    private envService: EnvironmentService
  ) {
    this.config = this.envService.config;
  }

  get nextButton$(): Observable<boolean> {
    return this._nextButton$.asObservable();
  }

  disableNextButton(validateNextButton: boolean) {
    this._nextButton$.next(validateNextButton);
  }

  // Separate from nextButton$/disableNextButton, which drive the Next button's own spinner while
  // it processes a real Next click - a child config panel saving itself should only disable Next,
  // never make it appear to be the one spinning.
  get childSaveInProgress$(): Observable<boolean> {
    return this.childSaves.pipe(map(saves => saves.size > 0), distinctUntilChanged());
  }

  childSaveInProgressFor$(projectId: string): Observable<boolean> {
    return this.childSaves.pipe(
      map(saves => [...saves.values()].includes(String(projectId))), distinctUntilChanged());
  }

  /** Each operation owns its cleanup; a late completion cannot clear a sibling's save. */
  beginChildSave(projectId: string): () => void {
    const token = {};
    const saves = new Map(this.childSaves.value);
    saves.set(token, String(projectId));
    this.childSaves.next(saves);
    return () => {
      if (!this.childSaves.value.has(token)) return;
      const remaining = new Map(this.childSaves.value);
      remaining.delete(token);
      this.childSaves.next(remaining);
    };
  }

  getDeployment(deploymentModel: string): string {
    const greenfield: string = 'greenfield';
    const brownfield: string = 'brownfield';
    switch (deploymentModel) {
      case 'deployMgntDomain':
        return greenfield;
      case 'convertMgntDomain':
        return brownfield;
    }
    return '';
  }

  getProductDetailsConfig(): Observable<any> {
    return of({
      deploymentModel: {
        default: {
          value: "deployMgntDomain",
          label: "Deploy a new Management domain"
        },
        supportedValues: [{
          value: "deployMgntDomain",
          label: "Deploy a new Management domain"
        }
        // , {
        //   value: "convertMgntDomain",
        //   label: "Convert an existing vCenter into a Management domain",
        //   disabled: true
        // }
        ]
      },
      architecture: {
        default: {
          value: "standard",
          label: "Yes (Standard Architecture)",
          description: "Choose standard architecture model if management and customer workloads cannot be co-located. Implement a standard architecture for workload provisioning and mobility across VMware Cloud Foundation instances according to production best practices."
        },
        supportedValues: [{
          value: "standard",
          label: "Yes (Standard Architecture)",
          description: "Choose standard architecture model if management and customer workloads cannot be co-located. Implement a standard architecture for workload provisioning and mobility across VMware Cloud Foundation instances according to production best practices."
        }
        // , {
        //   value: "consolidated",
        //   label: "No (Consolidated Architecture)",
        //   disabled: true,
        //   description: "Choose consolidated architecture model if management and customer workloads can be co-located. If you plan to deploy a small-scale environment, or if you are working on an SDDC proof-of-concept, implement a consolidated architecture."
        // }
      ],
        label: "Do you plan on deploying or importing additional workload domains?",
        required: true,
        learnMore: "Learn More"
      }
    })
  }

  createProjectPayload(projectFormValue: any, productFormValue: any, selectedDeployment: string, currentTab: any): VCPM.Metadata {
    let payload = { ...projectFormValue, ...productFormValue, deployment: selectedDeployment, currentTab: currentTab };
    payload.tags = this.checkForTags(payload.tags);
    return payload;
  }

  vsanArchType(architecture: string | null | undefined): string {
    let archToReturn: string = '';
    switch ((architecture)?.toLocaleLowerCase()) {
      case ("vSAN").toLocaleLowerCase():
      case ("vSAN OSA").toLocaleLowerCase():
      case ("OSA").toLocaleLowerCase():
        archToReturn = "vSAN OSA";
        break;
      case ("vSAN ESA").toLocaleLowerCase():
      case ("ESA").toLocaleLowerCase():
        archToReturn = "vSAN ESA";
        break;
    }
    return archToReturn;
  }

    groupComponents(components: PcsSizingEngineResponse.SoftwareComponent[] | undefined, availabilityTypeMap?: Map<string, string>): GroupedComponent[] {
    const map = new Map<string, GroupedComponent>();
    if (!components) {
      return [];
    }
    
    for (const comp of components) {
      const key = `${comp.productName}-${comp.brickSize}`;
      
      if (map.has(key)) {
        // Simply increment count for each component found
        map.get(key)!.count++;
      } else {
        // Create new entry with count 1
        const availabilityType = availabilityTypeMap?.get(`${comp.productName}-${comp.brickSize}`) || undefined;
        map.set(key, {
          name: comp.businessName,
          brickSize: comp.brickSize,
          count: 1,
          availabilityType: availabilityType
        });
      }
    }
    
    const result = Array.from(map.values());
    
    return result;
  }

    truncateLongString(str: string | undefined, lengthToShow: number) {
    if (str != null && str !== undefined && str !== '' && str.length > lengthToShow) {
      const strEllipses = str.substr(0, lengthToShow);
      return strEllipses + '...';
    } else {
      return str;
    }
  }

    checkForTags(tags: string) {
      if (!tags || tags.length === 0) {
        return [];
      }
      return Array.isArray(tags) ? tags : tags.split(',').map(element => element.trim());
    }

    getworkloadNameFromCode(workloadCode: string): string {
      return AppConstants.WORKLOADS.find(e => e.code == workloadCode)?.name ?? "";
    }

    getWorklaodTypeLabel(workloadType: string): string {
      let workloadTypes = AppConstants.DBW_SUPPORTED_WORKLOAD_TYPES.concat(AppConstants.GPW_SUPPORTED_WORKLOAD_TYPES).concat(AppConstants.VDI_SUPPORTED_WORKLOAD_TYPES);
      return workloadTypes.find(e => e.code == workloadType)?.name ?? ""; 
    }

}